/**
 * The person tier: what a verified address can reach, before it has chosen.
 *
 * Authority: IMPL-ENGAGE-002 §8.
 *
 * Sign-in used to resolve to an engagement — `order by created_at desc limit
 * 1` — so a contact on two live engagements reached the newest and the other
 * was, in the old comment's words, "reachable by being invited again". With
 * eight service lines, partner accounts and downstream forwarding, holding
 * several is now the normal case: the production backfill found one address on
 * four.
 *
 * **Multiplicity is normal; duplication is the pathology.** A pentest and a
 * vCISO retainer for the same customer are two engagements, correctly. The
 * chooser is not error handling, and nothing in the UI may present it as such.
 */
import { z } from 'zod';
import { CustomerRefSchema, EngagementOriginSchema, EngagementStateSchema } from './engagement.js';
import { NonEmptyTextSchema, TimestampSchema } from './primitives.js';
import { ServiceLineSchema } from './service.js';
/**
 * One row of the chooser. Deliberately narrow: no internal `ref`, no answers,
 * no commercial state. `customer_ref` identifies the row and is not a
 * credential — OTP plus a roster row still gate everything behind it.
 */
export const ReachableEngagementSchema = z.object({
    customer_ref: CustomerRefSchema,
    org_name: NonEmptyTextSchema,
    service_line: ServiceLineSchema,
    state: EngagementStateSchema,
    /** §8.3's "who created it": what the schema knows, naming no consultant. */
    origin: EngagementOriginSchema,
    /** Only the owner's row offers submit (§13 case 11: roles differ per engagement). */
    is_owner: z.boolean(),
    /**
     * Questions with at least one answer — a bare count, with no denominator.
     *
     * §8.3 asks for "34 of 58 answered" and there is no honest 58 to print: the
     * question set an engagement will be asked is computed by the engine from
     * visibility and branching at read time and is persisted in no table. The
     * first cut of the definer function used `count(*) from answers` as the
     * total, which counts revisions — 254 against 156 real questions on the
     * largest live engagement. Migration 20260825000007 removed it rather than
     * ship a denominator that misinforms the one screen whose job is telling
     * two engagements apart.
     */
    answered: z.number().int().nonnegative(),
    /** Questions forwarded to this person specifically, still open. */
    open_for_me: z.number().int().nonnegative(),
    /**
     * Whether the session is currently bound to this engagement (§8.3).
     *
     * Nothing else on the customer plane names it: `/me/engagements` lists what
     * the address can REACH and `progress` carries the viewer's email and no
     * organisation, so the switcher could only ever say "Switch engagement" —
     * a verb with no subject, on every screen. Computed from
     * `current_engagement_id()`, which is already on the transaction.
     *
     * Exactly one row can carry it, and none is legitimate: the projection drops
     * closed engagements, so a session bound to one that has since closed
     * reports no current row rather than naming the wrong one.
     */
    is_current: z.boolean(),
    /**
     * §5.3, derived and never stored: sector, size band and the owner's phone
     * are all present. A consultant creating an engagement on a call knows the
     * organisation and often nothing else, so `false` is the normal opening
     * state and is what sends the owner through confirm mode (§6.3).
     */
    profile_complete: z.boolean(),
    created_at: TimestampSchema,
});
export const ReachableEngagementsSchema = z.array(ReachableEngagementSchema);
/** Exchanging a selection token for a real, engagement-bound session. */
export const SessionSelectRequestSchema = z.object({
    customer_ref: CustomerRefSchema,
});
//# sourceMappingURL=person.js.map